/**
 * The card a person decides on (ADR-0024).
 *
 * It sits in the task drawer beside the evidence, because the two are the two
 * halves of one gate: the machine checks what Kimene observed, and a person
 * signs off what that means. Neither substitutes for the other, and this card is
 * the second half made visible.
 *
 * It says in advance whether *you* may decide. A reviewer who reads a whole
 * financial card and then discovers a 403 has been told the wrong thing at the
 * wrong time; the server answers `youMayDecide` and this renders the reason.
 */

import { useState } from 'react'

import { Badge, Button, Panel } from '@/components/primitives'
import { Notice, useApiErrorMessage } from '@/components/controls'
import { useT, useLocale } from '@/i18n/context'
import { formatRelative } from '@/i18n'
import { riskTone } from '@/lib/tone'
import { useApproval, useApprovalMutations } from './workData'
import type { ID } from '@/types/domain'
import './ApprovalCard.css'

export function ApprovalCard({ taskId, approvalId }: { taskId: ID; approvalId: ID }) {
  const t = useT()
  const { locale } = useLocale()
  const describe = useApiErrorMessage()
  const approval = useApproval(approvalId)
  const { decide } = useApprovalMutations(taskId)
  const [note, setNote] = useState('')

  const a = approval.data
  if (!a) return null

  return (
    <Panel className="k-appr" title={t('approvals.card')}>
      <div className="k-appr__head">
        <Badge tone={riskTone(a.riskLevel)} mono>
          {a.riskLevel}
        </Badge>
        <span className="k-appr__rule">{ruleLabel(a.rule, t)}</span>
        <span className="k-mono k-appr__age">{formatRelative(a.raisedAt, locale)}</span>
      </div>

      <p className="k-appr__why">{a.justification}</p>

      {a.affectedResources.length > 0 && (
        <div className="k-appr__block">
          <span className="k-label">{t('approvals.affects')}</span>
          <ul className="k-appr__list">
            {a.affectedResources.map((res) => (
              <li key={res} className="k-mono">
                {res}
              </li>
            ))}
          </ul>
        </div>
      )}

      {a.consequenceIfDenied && (
        <div className="k-appr__block">
          <span className="k-label">{t('approvals.ifDenied')}</span>
          <p className="k-appr__denied">{a.consequenceIfDenied}</p>
        </div>
      )}

      {a.requestedFor && (
        <p className="k-appr__for">{t('approvals.requestedFor', { actor: a.requestedFor })}</p>
      )}

      {decide.error && <Notice tone="err">{describe(decide.error).title}</Notice>}

      {a.state !== 'PENDING' ? (
        <Notice tone={a.state === 'APPROVED' ? 'ok' : 'warn'}>
          {t(`approvals.state.${a.state}`)}
          {a.decidedBy && ` — ${t('approvals.decidedBy', { actor: a.decidedBy })}`}
          {a.decisionNote && `: ${a.decisionNote}`}
        </Notice>
      ) : !a.youMayDecide ? (
        // Said plainly, and before the button, rather than discovered as a 403.
        <Notice tone="warn">
          {t('approvals.mayNot', { permissions: a.requiredPermissions.join(', ') })}
        </Notice>
      ) : (
        <div className="k-appr__decide">
          <label className="k-appr__notelabel">
            <span className="k-label">{t('approvals.note')}</span>
            <textarea
              className="k-appr__note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
          </label>
          <div className="k-appr__buttons">
            <Button
              variant="primary"
              busy={decide.isPending}
              onClick={() => decide.mutate({ id: a.id, decision: 'approve', note })}
            >
              {t('approvals.approve')}
            </Button>
            <Button
              busy={decide.isPending}
              onClick={() => decide.mutate({ id: a.id, decision: 'reject', note })}
            >
              {t('approvals.reject')}
            </Button>
          </div>
        </div>
      )}
    </Panel>
  )
}

/** The rule in words.
 *
 *  Switched rather than interpolated into a key, so the i18n contract stays a
 *  compile-time one: a rule this build predates renders as its own name, which
 *  is a fact, instead of as a missing-key placeholder. */
function ruleLabel(rule: string, t: ReturnType<typeof useT>): string {
  switch (rule) {
    case 'risk.l3-requires-approval':
      return t('approvals.rule.risk.l3-requires-approval')
    case 'risk.l4-requires-approval':
      return t('approvals.rule.risk.l4-requires-approval')
    case 'evidence.human-approval-required':
      return t('approvals.rule.evidence.human-approval-required')
  }
  return rule
}
